import React, { useEffect, useRef, useState } from 'react'
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  Image,
  TouchableOpacity,
  NativeScrollEvent,
  NativeSyntheticEvent,
} from 'react-native'
import { useNavigation, useRoute, useTheme } from '@react-navigation/native'
import { Header, Notes, SearchBar } from '@components'
import { Theme } from '@theme'
import { useNotes } from './hooks'
import { Contact, useGetContactById, useScreen, usePaginateContacts } from '@hooks'
import { EditNote } from './EditNote'
import { getCommonStyles, Scope } from '@utils'
import ContactsFilter from '../components/ContactsFilter'
import { ContactsList } from '../../contacts/listingContacts/components'
import { SafeAreaView } from 'react-native-safe-area-context'
import { ActivityIndicator } from 'react-native'
import { defaultImage } from '@assets'
import { useLanguage } from '@context'

// How far from the bottom of the contact column the next page starts loading.
const LOAD_MORE_THRESHOLD = 120
// A page of the column that failed is asked for again after 5 s, 10 s, 20 s,
// then every 30 s.
const NEXT_PAGE_RETRY_DELAY_MS = 5000
const NEXT_PAGE_RETRY_DELAY_MAX_MS = 30000

const SelectedContactDisplay: React.FC<{
  contactId: string
  onPress: () => void
}> = ({ contactId, onPress }) => {
  const theme = useTheme()
  const styles = getStyles(theme)
  const commonStyles = getCommonStyles(theme)
  const { data: contact, isLoading } = useGetContactById(contactId)

  if (isLoading) {
    return (
      <View style={[commonStyles.row, styles.contactContainer]}>
        <ActivityIndicator size="small" />
      </View>
    )
  }

  if (!contact) {
    return null
  }

  return (
    <TouchableOpacity style={[commonStyles.row, styles.contactContainer]} onPress={onPress}>
      <Image
        source={contact.avatarUrl ? { uri: contact.avatarUrl } : defaultImage}
        style={styles.contactAvatar}
      />
      <Text style={styles.contactName}>{contact.fullName}</Text>
    </TouchableOpacity>
  )
}

export const NotesScreen: React.FC = () => {
  const navigation = useNavigation<any>()
  const route = useRoute<any>()
  const profileId = route.params?.profileId
  const theme = useTheme()
  const { t } = useLanguage()
  const commonStyles = getCommonStyles(theme)
  const styles = getStyles(theme)
  const { isWeb, isMobile, isDesktop } = useScreen()

  const [selectedNoteId, setSelectedNoteId] = useState<string | null>(null)
  // Counts the « + » of the computer: with the note's id, it gives each note
  // opened on the right its own fresh editor. One editor kept for several notes
  // showed the previous note's text in the new one.
  const [newNoteCount, setNewNoteCount] = useState(0)

  // Hook pour gérer les notes
  const {
    selectedContactId,
    scopes,
    searchText,
    peopleSearchText,
    setSearchText,
    setPeopleSearchText,
    setSelectedContactId,
    setScopes,
  } = useNotes()

  const hasGhost = scopes.includes(Scope.GHOST)
  const hasPrivate = scopes.includes(Scope.PRIVATE)
  const hasProfessional = scopes.includes(Scope.PROFESSIONAL)
  // Same chips as the contacts list, applied to the notes: « Toutes » (no chip)
  // shows every note; Privé / Pro keep that scope; the ghost chip keeps only
  // the notes about ghost profiles.
  const notesFilters = {
    q: searchText,
    contactId: selectedContactId,
    scope: hasPrivate ? Scope.PRIVATE : hasProfessional ? Scope.PROFESSIONAL : undefined,
    onlyGhost: hasGhost || undefined,
    noGhost: ((hasPrivate || hasProfessional) && !hasGhost) || undefined,
  }
  const {
    flatData: contacts,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    isFetching,
    fetchNextPage,
  } = usePaginateContacts({
    scope: hasPrivate ? Scope.PRIVATE : hasProfessional ? Scope.PROFESSIONAL : undefined,
    // Ghost is a bonus filter (same behaviour as the main contacts list):
    // selected -> only ghost profiles; unselected -> real contacts (ghosts are
    // excluded only when a Privé/Pro tab is active).
    onlyGhost: hasGhost || undefined,
    noGhost: ((hasPrivate || hasProfessional) && !hasGhost) || undefined,
    search: peopleSearchText,
  })

  // On the computer the contacts sit inside the column's scroll view: their own
  // list never reaches its end, so the following pages were never asked for and
  // only the first ten people showed. They are asked for from here: near the
  // bottom of the column, and for as long as the people do not fill it.
  const [columnHeight, setColumnHeight] = useState(0)
  // The height of the people, and how many of them it was measured with (a
  // height from before the last page arrived says nothing about this one).
  const [peopleLayout, setPeopleLayout] = useState({ height: 0, count: -1 })
  const peopleCount = contacts?.length ?? 0
  const loadMorePeople = () => {
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage()
  }
  const onColumnScroll = ({ nativeEvent }: NativeSyntheticEvent<NativeScrollEvent>) => {
    const { layoutMeasurement, contentOffset, contentSize } = nativeEvent
    const isNearBottom =
      layoutMeasurement.height + contentOffset.y >= contentSize.height - LOAD_MORE_THRESHOLD
    if (isNearBottom) loadMorePeople()
  }
  const nextPageRetriesRef = useRef(0)
  useEffect(() => {
    if (!isDesktop || columnHeight === 0 || peopleLayout.count !== peopleCount) return
    // Nothing is asked for while the list is being fetched: asking for a page
    // would cut short a refresh under way.
    if (isFetching) return
    const isColumnFilled = peopleLayout.height > columnHeight
    if (!isFetchNextPageError) {
      nextPageRetriesRef.current = 0
      if (!isColumnFilled) loadMorePeople()
      return
    }
    // A page failed. It is not asked for again at once (it was, again and
    // again, for as long as the server was down). When the people fill the
    // column, a scroll to the bottom asks for it; when they do not, nothing
    // can be scrolled: it is asked for from here, less and less often.
    if (!hasNextPage || isColumnFilled) return
    const delay = Math.min(
      NEXT_PAGE_RETRY_DELAY_MS * 2 ** nextPageRetriesRef.current,
      NEXT_PAGE_RETRY_DELAY_MAX_MS,
    )
    const timer = setTimeout(() => {
      nextPageRetriesRef.current += 1
      void fetchNextPage()
    }, delay)
    return () => clearTimeout(timer)
  }, [
    isDesktop,
    columnHeight,
    peopleLayout,
    peopleCount,
    hasNextPage,
    isFetching,
    isFetchNextPageError,
  ])

  const handleAddNote = () => {
    if (!isDesktop) {
      // Straight to the note: the person is chosen inside it (« À propos de »).
      navigation.navigate('Menu', {
        screen: 'Notes',
        params: {
          screen: 'EditNote',
        },
      })
    } else {
      setSelectedNoteId('')
      setNewNoteCount((count) => count + 1)
    }
  }

  const goToContact = () => {
    navigation.navigate('Profile', {
      screen: 'DetailsGhostProfile',
      params: { profileId: selectedContactId },
    })
  }
  const handleContactPress = (contact: Contact) => {
    if (selectedContactId === contact.id) {
      setSelectedContactId(undefined)
    } else {
      setSelectedContactId(contact.id)
    }
  }

  const handleNotePress = (noteId: string) => {
    setSelectedNoteId(noteId)
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={isDesktop ? commonStyles.flex315Bordered : commonStyles.flex}>
        <Header
          title={t('notesScreen.title')}
          iconLeft={isWeb ? undefined : 'arrowBack'}
          iconRight={'plus'}
          onPressRight={handleAddNote}
        />
        {!isDesktop && (
          <View style={[styles.searchContainerMobile, !isMobile && commonStyles.row]}>
            <View
              style={[
                styles.searchContainer,
                !isMobile && commonStyles.flex,
                !isMobile && commonStyles.searchContainerMaxWidth,
                !isMobile && styles.noMarginTop,
              ]}
            >
              <SearchBar value={searchText} onChangeText={setSearchText} />
            </View>
            <View style={styles.filtersContainer}>
              <ContactsFilter
                scope={scopes}
                onScopeChange={setScopes}
                allLabel={t('notesScreen.allNotes')}
              />
            </View>
          </View>
        )}
        <ScrollView
          style={styles.notesList}
          contentContainerStyle={isWeb ? styles.fullHeight : styles.grow}
          showsVerticalScrollIndicator={false}
          onScroll={isDesktop ? onColumnScroll : undefined}
          scrollEventThrottle={100}
          onLayout={({ nativeEvent }) => setColumnHeight(nativeEvent.layout.height)}
        >
          <View style={isDesktop && styles.notesListContainer}>
            {isDesktop && (
              <View
                style={commonStyles.flex}
                onLayout={({ nativeEvent }) =>
                  setPeopleLayout({ height: nativeEvent.layout.height, count: peopleCount })
                }
              >
                <View style={styles.searchContainerMobile}>
                  <View style={styles.searchContainer}>
                    <SearchBar
                      value={peopleSearchText}
                      onChangeText={setPeopleSearchText}
                      placeholderText={t('notesScreen.searchPeople')}
                    />
                  </View>
                  <View style={styles.filtersContainer}>
                    <ContactsFilter
                      scope={scopes}
                      onScopeChange={setScopes}
                      allLabel={t('notesScreen.allNotes')}
                    />
                  </View>
                </View>
                <ContactsList
                  noRightSection
                  contacts={contacts || []}
                  selectedContactId={selectedContactId}
                  onContactPress={handleContactPress}
                  // After a page that failed the list does not ask for it
                  // again on its own (it did, each time its spinner went).
                  hasNextPage={hasNextPage && !isFetchNextPageError}
                  isFetchingNextPage={isFetchingNextPage}
                  isFetching={isFetching}
                  fetchNextPage={fetchNextPage}
                />
              </View>
            )}
            {!isDesktop && (
              <Notes
                profileId={profileId}
                filters={notesFilters}
                grouped
                onNotePress={isDesktop ? handleNotePress : undefined}
              />
            )}
          </View>
        </ScrollView>
      </View>
      {isDesktop && selectedNoteId === null && (
        <View style={commonStyles.flex}>
          <View style={[styles.searchContainer, styles.notesSearch]}>
            <SearchBar
              value={searchText}
              onChangeText={setSearchText}
              placeholderText={t('notesScreen.searchNotes')}
              hasMaxWidth
            />
          </View>
          {selectedContactId && (
            <SelectedContactDisplay contactId={selectedContactId} onPress={goToContact} />
          )}
          <Notes
            profileId={profileId}
            filters={notesFilters}
            grouped
            onNotePress={isDesktop ? handleNotePress : undefined}
          />
        </View>
      )}
      {isDesktop && selectedNoteId !== null && (
        <EditNote
          // The editor that closes saves what was left in it.
          key={`${selectedNoteId}-${newNoteCount}`}
          noteId={selectedNoteId || undefined}
          onComplete={() => setSelectedNoteId(null)}
          onBack={() => setSelectedNoteId(null)}
          contactId={selectedContactId}
        />
      )}
    </SafeAreaView>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    container: {
      flex: 1,
      flexDirection: 'row',
      backgroundColor: '#fff',
    },
    searchContainerMobile: {
      marginBottom: theme.spacing.md,
    },
    searchContainerWeb: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: theme.spacing.lg,
    },
    searchContainer: {
      paddingHorizontal: theme.spacing.lg,
      paddingBottom: theme.spacing.md,
    },
    noMarginTop: {
      paddingBottom: 0,
    },
    notesSearch: {
      paddingTop: theme.spacing.lg,
      paddingBottom: 0,
    },
    filtersContainer: {
      paddingHorizontal: theme.spacing.lg,
    },
    notesListContainer: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'flex-start',
    },
    notesList: {
      flex: 1,
    },
    fullHeight: {
      height: '100%',
    },
    // On the phone app a content box as tall as the screen cannot be scrolled:
    // it only has to be at least that tall.
    grow: {
      flexGrow: 1,
    },
    loadingContainer: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
    },
    errorContainer: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: 40,
    },
    errorText: {
      fontSize: theme.fontSize.medium,
      color: theme.colors.error || theme.colors.text,
      textAlign: 'center',
    },
    contactContainer: {
      alignItems: 'center',
      justifyContent: 'center',
      margin: theme.spacing.md,
    },
    contactAvatar: {
      width: 30,
      height: 30,
      borderRadius: 15,
      marginRight: theme.spacing.sm,
    },
    contactName: {
      fontSize: theme.fontSize.medium,
      color: theme.colors.text,
    },
  })
